import { Feature, Geometry } from "geojson";
import mapboxgl, { SymbolLayout, SymbolPaint } from "mapbox-gl";

const EMPTY_FC = { type: "FeatureCollection" as const, features: [] };

/**
 * Tracks validation layer IDs that are currently on the map.
 * Used by moveDrawLayersToTop (MultiLayerEditHandler) to keep validation layers above draw layers.
 */
export const activeValidationLayerIds = new Set<string>();

/**
 * Ensures a GeoJSON source exists on the map for validation indicators.
 * Creates the source with an empty FeatureCollection if it doesn't exist.
 */
export const ensureValidationSource = (map: mapboxgl.Map, sourceId: string): void => {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, { type: "geojson", data: EMPTY_FC });
  }
};

/**
 * Adds a validation symbol layer to the map.
 * Creates the source if it doesn't exist.
 */
export const ensureValidationSymbolLayer = (map: mapboxgl.Map, sourceId: string, layerId: string, layout: SymbolLayout, paint: SymbolPaint): void => {
  ensureValidationSource(map, sourceId);
  if (!map.getLayer(layerId)) {
    map.addLayer({ id: layerId, type: "symbol", source: sourceId, layout, paint });
  }
  activeValidationLayerIds.add(layerId);
};

/**
 * Updates the features in a validation source.
 */
export const setValidationFeatures = (map: mapboxgl.Map, sourceId: string, features: Array<Feature<Geometry>>): void => {
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData({ type: "FeatureCollection", features });
};

/**
 * Removes a validation layer and its source from the map.
 */
export const removeValidationLayer = (map: mapboxgl.Map, sourceId: string, layerId: string): void => {
  if (map.getLayer(layerId)) map.removeLayer(layerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
  activeValidationLayerIds.delete(layerId);
};
